import { useEffect, useMemo, useState } from "react";

import { Box, Dialog, Typography } from "@mui/material";
import map from "lodash/map";

import { metricSplittingDataCy, metricSplittingTxt } from "../../assets/texts/CloudAnalytics";
import { useMetricSplitsContext } from "../../Pages/CloudAnalytics/Context";
import { type Step, Stepper, type StepState } from "../Stepper";
import ItemToSplit from "./ItemToSplit";
import SplitCostTargets from "./SplitCostTargets";
import SplitDataStore from "./SplitDataStore";
import { type Split, type SplitMode, type SplitTarget } from "./types";
import { updateSplitDataWithInvalidTargetIDs } from "./utils";

const SplitCostModal = () => {
  const {
    onMetricSplitDataSubmit,
    getInvalidTargetsIDs,
    metricSplitModalData,
    metricSplitLabel,
    metricSplits,
    onClose,
  } = useMetricSplitsContext();
  const selectedOrigins = useMemo(() => new Set(map(metricSplits, "origin.id")), [metricSplits]);
  const [stepperState, setStepperState] = useState<StepState[]>(["incomplete", "incomplete"]);
  const [splitData, setSplitData] = useState<Split | undefined>();
  const [step, setStep] = useState(0);
  const originalTargets = metricSplitModalData?.targets ?? [];
  const [currentStep, getCurrentStep] = useState(0);

  const [isSubmitDisabled, submitTooltip] = useMemo(() => {
    if (!splitData?.targets?.some((target) => target.checked)) {
      return [true, metricSplittingTxt.SPLIT_COST_TARGETS.VALIDATION.SELECT_TARGET];
    }

    const hasNegativeValues = splitData.targets.some(
      (target) => target.checked && Number(target.rawValue ?? target.value) < 0
    );
    if (hasNegativeValues) {
      return [true, metricSplittingTxt.SPLIT_COST_TARGETS.VALIDATION.NEGATIVE_VALUES];
    }

    const hasZeroValue = splitData.targets.some(
      (target) => target.checked && Number(target.rawValue ?? target.value) === 0
    );
    if (hasZeroValue) {
      return [true, metricSplittingTxt.SPLIT_COST_TARGETS.VALIDATION.ZERO_VALUE];
    }

    const sum = splitData.targets.reduce((acc, target) => {
      if (target.checked) {
        return acc + Number(target.rawValue ?? target.value);
      }
      return acc;
    }, 0);

    if (Math.abs(sum - 100) >= 0.01) {
      return [true, metricSplittingTxt.SPLIT_COST_TARGETS.VALIDATION.TOTAL_ALLOCATION];
    }

    return [false, ""];
  }, [splitData?.targets]);

  useEffect(() => {
    // check if the split already exists -
    // then, shows the second step
    if (metricSplitModalData?.origin && metricSplitModalData?.mode) {
      setStep(1);
    }

    if (metricSplitModalData?.targets) {
      const convertedData = {
        ...metricSplitModalData,
        targets: metricSplitModalData.targets.map((target) => ({
          ...target,
          value: String(Number(target.value) * 100),
          rawValue: String(Number(target.value) * 100),
        })),
      };
      setSplitData(convertedData);
    } else {
      setSplitData(metricSplitModalData);
    }
  }, [metricSplitModalData]);

  const [origin, setOrigin] = useState<SplitTarget>();

  const handleNewSplitData = (newSplitData: Split): void => {
    setSplitData(newSplitData);
  };

  useEffect(() => {
    if (splitData?.origin && splitData.origin !== origin) {
      setOrigin(splitData.origin);
      updateSplitDataWithInvalidTargetIDs({ splitData, handleNewSplitData, getInvalidTargetsIDs });
    }
  }, [splitData, origin, getInvalidTargetsIDs]);

  const onSplitModeChanged = (mode: SplitMode) => {
    setSplitData(SplitDataStore.modeChange(mode)(splitData));
  };

  const onSelectOrigin = (target?: SplitTarget) => {
    setSplitData(SplitDataStore.selectOrigin(target, originalTargets));
  };

  const onCheckedTarget = (label: string) => {
    setSplitData(SplitDataStore.checkedTarget(label));
  };

  const handleCheckAll = (checked: boolean) => {
    setSplitData(SplitDataStore.checkAllTargets(checked));
  };

  const onSubmit = async () => {
    setSplitData((prev) => {
      if (!prev) return prev;

      let data: Split = prev;
      if (prev.mode === "proportional") {
        const updatedData = SplitDataStore.autoDistribute(prev);
        if (updatedData) data = updatedData;
      }

      if (data.targets && Array.isArray(data.targets)) {
        data = {
          ...data,
          targets: data.targets.map((target) => ({
            ...target,
            value: String(Number(target.value) / 100),
          })),
        };
      }

      onMetricSplitDataSubmit(data);
      return data;
    });
  };

  const onChangePercentage = (id: string, value: string, rawValue?: string) => {
    setSplitData(SplitDataStore.updatePercentage(id, value, rawValue));
  };

  useEffect(() => {
    const firstStepState = splitData?.origin ? "complete" : "incomplete";
    const secondStepState = currentStep === 1 && !isSubmitDisabled ? "complete" : "incomplete";
    setStepperState([firstStepState, secondStepState]);
  }, [currentStep, isSubmitDisabled, splitData?.origin]);

  const stepComponents = [
    <ItemToSplit
      key="itemToDistribute"
      options={originalTargets}
      isOptionDisabled={(target) => target.id !== origin?.id && selectedOrigins.has(target.id)}
      onSelectOrigin={onSelectOrigin}
      origin={splitData?.origin}
    />,
    <SplitCostTargets
      key="splitCostTargets"
      checkAll={handleCheckAll}
      onCheckedTarget={onCheckedTarget}
      splitData={splitData}
      origin={splitData?.origin}
      splitMode={splitData?.mode}
      onSplitModeChanged={onSplitModeChanged}
      onChangePercentage={onChangePercentage}
      setSplitData={setSplitData}
    />,
  ];

  const steps: Step[] = [
    {
      children: stepComponents[0],
      label: metricSplittingTxt.ITEM_TO_SPLIT.TITLE,
      order: 0,
      required: true,
      state: stepperState[0],
    },
    {
      children: stepComponents[1],
      label: metricSplittingTxt.SPLIT_COST_TARGETS.TITLE,
      order: 1,
      required: true,
      state: stepperState[1],
    },
  ];

  const lowerCaseMetricSplitLabel = (metricSplitLabel || "").toLowerCase();
  return (
    <Dialog
      open={!!metricSplitModalData}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-cy={metricSplittingDataCy.MODAL}
    >
      <Box>
        <Typography
          variant="h3"
          sx={{
            px: 3,
            pt: 2,
            pb: 3,
          }}
        >
          {metricSplittingTxt.SPLIT} {lowerCaseMetricSplitLabel}
        </Typography>

        <Stepper
          loading={false}
          onCancel={onClose}
          onSubmit={onSubmit}
          steps={steps}
          submitButtonLabel={`${metricSplittingTxt.SPLIT} ${lowerCaseMetricSplitLabel}`}
          relativeFooter={true}
          disableSubmit={isSubmitDisabled}
          submitTooltip={submitTooltip}
          contentSx={{
            px: 3,
            mb: 3,
          }}
          overrideStep={step}
          getCurrentStep={getCurrentStep}
        />
      </Box>
    </Dialog>
  );
};

export default SplitCostModal;
